// RAGADÓS SZEKCIÓ-NAVIGÁCIÓ („on this page") — a detail-oldal szekcióihoz.
//
// MIÉRT EZ, ÉS NEM TAB. A tab akkor jó, ha a szakaszok egyenrangú, egymást KIZÁRÓ munkaterületek. Egy
// entitás-detailen nem ezek: van EGY űrlap (saját mentés-gombbal) és néhány kapcsolódó lista. Tabbal
// (a) az „Alapadatok" egy fül lenne a többi közt, pedig az az oldal lényege, (b) elrejtőzne, MI tartozik az
// entitáshoz (ma egy görgetéssel látszik), (c) a mentetlen űrlap + fülváltás rossz párosítás, (d) a fül
// deep-linkelhető állapotot követel. Ez a rail a tab NAVIGÁCIÓS előnyét adja (ugrás görgetés helyett) a
// hátrányai nélkül: a tartalom EGY lapon marad. Ugyanez a minta a dokumentáció-oldalak és a modern
// rekord-nézetek (Linear, Stripe) megoldása; a tabos rekord-oldal a régebbi generáció.
//
// HOL LAKIK: a `FormPage` `aside` slotjában, a keskeny űrlap-hasáb MELLETTI üres sávban — nem szorítja
// össze a tartalmat, csak kihasználja a helyet. `lg` alatt REJTETT: keskeny kijelzőn a rail vízszintes
// sávvá esne szét (pont a tab-sor hátránya), a görgetés viszont ott természetes.
//
// AKTÍV SZEKCIÓ: GEOMETRIAI mérés egy rAF-fel fojtott, passzív scroll-figyelőn — nem `IntersectionObserver`.
//
// Az IO elsőre kézenfekvőbb, de a „melyik szekciónál járok" kérdésre rosszul felel: a callback CSAK a VÁLTOZÓ
// elemeket adja (egy „X kilépett" hívásnál a már bent lévő Y-t nem látjuk → a jelölő beragad), egyszerre több
// szekció is a sávban lehet, és a lap alján lévő RÖVID szekció sosem ér fel a sávba (klasszikus TOC-hiba: az
// utolsó menüpont holt marad). Mindháromra külön kerülő kellett volna. A szabály viszont egy mondat, ha
// egyszerűen MEGMÉRJÜK:
//
//   aktív = az UTOLSÓ szekció, aminek a teteje már az AKTIVÁLÁSI VONAL fölött van
//           (+ ha a görgetés a lap alján áll, mindig az utolsó).
//
// Determinisztikus, minden élhelyzetre ugyanaz a képlet, és 6 szekciónál a mérés elhanyagolható (rAF-fojtás →
// legfeljebb képkockánként egyszer). A görgetés-konténer NEM az ablak (a `FormPage` saját `overflow-y-auto`
// sávja), ezért minden pozíció AHHOZ képest számítódik.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFormPageScrollRoot } from '@/components/shared/form-page';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';

export interface SectionNavItem {
  /** A szekció `id`-ja (a `FormSection id=…`-jával EGYEZŐ) — ez köti össze a railt a tartalommal. */
  id: string;
  label: string;
}

/** Az aktiválási vonal a görgetés-port tetejétől — ami efölé ér, azt tekintjük „itt járok"-nak. */
const ACTIVATION_RATIO = 0.33;

export function SectionNav({
  items,
  ariaLabel,
}: {
  items: readonly SectionNavItem[];
  ariaLabel: string;
}): JSX.Element | null {
  const scrollRoot = useFormPageScrollRoot();
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
  // A legutóbb KATTINTOTT cél: amíg a görgetés tart, az observer köztes szekciókon is átfut, és a jelölő
  // „átugrálna" a cél előtt. Ezért kattintás után a célt tartjuk, amíg tényleg oda nem érünk.
  const pendingTarget = useRef<string | null>(null);

  const ids = useMemo(() => items.map((i) => i.id).join('|'), [items]);

  useEffect(() => {
    // Üres lista → NINCS mit figyelni. Enélkül a `''.split('|')` egy üres id-t adna, abból `querySelector('#')`
    // lenne, ami kivételt dob (a komponens `null`-t rendel ilyenkor, de a hook akkor is lefut).
    if (!scrollRoot || ids === '') return;
    const elements = ids
      .split('|')
      .map((id) => scrollRoot.querySelector<HTMLElement>(`#${CSS.escape(id)}`))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;

    let frame = 0;
    const measure = (): void => {
      frame = 0;
      const rootTop = scrollRoot.getBoundingClientRect().top;
      const line = scrollRoot.clientHeight * ACTIVATION_RATIO;
      // A lap alján MINDIG az utolsó szekció aktív: egy rövid záró-szekció (a veszélyzóna) különben sosem
      // érné el az aktiválási vonalat, és a menüpontja holt maradna. 2px tűrés a tört pixelekre.
      const atBottom =
        scrollRoot.scrollTop + scrollRoot.clientHeight >= scrollRoot.scrollHeight - 2;

      let next = elements[0]?.id ?? null;
      if (atBottom) {
        next = elements[elements.length - 1]?.id ?? next;
      } else {
        for (const el of elements) {
          if (el.getBoundingClientRect().top - rootTop <= line) next = el.id;
          else break; // DOM-sorrendben haladunk: az első vonal ALATTI után már nincs korábbi
        }
      }
      if (!next) return;
      // Kattintás utáni görgetés közben a mérés a KÖZTES szekciókat is látja — a jelölő „átugrálna" a cél
      // előtt. Ezért a célt tartjuk, amíg tényleg oda nem érünk.
      if (pendingTarget.current && pendingTarget.current !== next) return;
      pendingTarget.current = null;
      setActiveId(next);
    };

    /** rAF-fojtás: görgetés közben legfeljebb képkockánként EGYSZER mérünk. */
    const onScroll = (): void => {
      if (frame === 0) frame = requestAnimationFrame(measure);
    };

    measure();
    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    // Átméretezés / elrendezés-változás (pl. egy szekció adata betöltődött) is elmozdítja a szekciókat.
    const resizeObserver = new ResizeObserver(onScroll);
    resizeObserver.observe(scrollRoot);
    for (const el of elements) resizeObserver.observe(el);
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      scrollRoot.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
    };
  }, [scrollRoot, ids]);

  const jumpTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    pendingTarget.current = id;
    setActiveId(id); // azonnali visszajelzés — ne a görgetés végén ugorjon a jelölő
    // A `prefers-reduced-motion` tiszteletben tartása: akinek a mozgás zavaró, annak ugrás, nem animáció.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    // Fókusz a célra, hogy a billentyűzetes olvasás is ODA folytatódjon (nem a rail elejére ugrik vissza).
    el.focus({ preventScroll: true });
  }, []);

  if (items.length === 0) return null;

  return (
    <nav aria-label={ariaLabel} className="sticky top-6 w-52">
      {/* A függőleges vezetővonal a lista bal szélén fut; az aktív elem egy BRAND-színű szakasszal fedi le —
          a dokumentáció-oldalak bevált jelölése, visszafogott kivitelben (a lap nyugodt marad). */}
      <ul className="border-border/60 space-y-0.5 border-l">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <li key={item.id} className="relative">
              {active && (
                <span
                  aria-hidden="true"
                  className="bg-brand absolute -left-px bottom-1 top-1 w-0.5 rounded-full"
                />
              )}
              <button
                type="button"
                onClick={() => jumpTo(item.id)}
                aria-current={active ? 'true' : undefined}
                className={cn(
                  'focus-visible:ring-ring block w-full rounded-r-md py-1.5 pl-3 pr-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2',
                  active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {/* Ugyanaz az `overline` tipográfia, mint a szekció-CÍMEKÉ — a rail a címek kicsinyített
                    tükre, nem egy külön vizuális nyelv. */}
                <Text
                  variant="overline"
                  as="span"
                  className={cn('block truncate', active && 'text-foreground')}
                >
                  {item.label}
                </Text>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
